import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CiZoomIn, CiZoomOut } from "react-icons/ci";
import { LuUndo2 } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import {
  setStatistics,
  setIsLoading,
  clearHistory,
  clearStatistics,
} from "../../store/slices/dartSlice";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { transformRounds } from "../../utils/common";
import { GoTrash } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import { Transition } from "@headlessui/react";

const Controls = ({ toggleZoom, undoHit, isZoomed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const roundHistory = useSelector((state) => state.dartGame.roundHistory);
  const minDartRounds = useSelector((state) => state.dartGame.minDartRounds);
  const dartCount = useSelector((state) => state.dartGame.dartCount);
  const proMember = useSelector((state) => state.user.proMember);
  const evaluateIsDisabled = dartCount >= minDartRounds * 3;
  const [showEvaluatePopup, setShowEvaluatePopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [popupViewed, setPopupViewed] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem("evaluationPopupViewed");
    return stored === "true";
  });

  // Update localStorage when popupViewed changes
  useEffect(() => {
    localStorage.setItem("evaluationPopupViewed", popupViewed);
  }, [popupViewed]);

  // Show popup automatically when evaluation is ready, but only if it hasn't been viewed before
  useEffect(() => {
    if (evaluateIsDisabled && !popupViewed) {
      setShowEvaluatePopup(true);
      setPopupDismissed(false);
    }
  }, [evaluateIsDisabled, popupViewed]);

  function handleDisabledEvent(event) {
    event.preventDefault();
  }

  function handleClearHistory() {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all inserted data?"
    );
    if (confirmDelete) {
      dispatch(clearHistory());
      dispatch(clearStatistics());
      // Reset popup viewed state when history is cleared
      setPopupViewed(false);
      localStorage.removeItem("evaluationPopupViewed");
    }
  }

  const handleCalculateStatistics = (mode = "session") => {
    dispatch(setIsLoading(true));
    navigate("/statistics");

    if (!proMember) {
      // Use dummy values for non-pro members
      const dummyStatistics = {
        avg_median: 65.5,
        double_hit: 45.2,
        D20_or_D16: "D20",
        D20_or_D16_likelihood: 68.3,
        T20_or_T19: "T20",
        T20_or_T19_likelihood: 72.1,
        checkout_success: 58.7,
        checkout_attempts: 12,
        checkout_success_rate: 58.7,
        checkout_avg_darts: 2.8,
        checkout_most_common: "D16",
        checkout_most_common_count: 5,
        checkout_most_common_percentage: 41.7,
        checkout_second_most_common: "D20",
        checkout_second_most_common_count: 3,
        checkout_second_most_common_percentage: 25.0,
        checkout_third_most_common: "D8",
        checkout_third_most_common_count: 2,
        checkout_third_most_common_percentage: 16.7,
        checkout_fourth_most_common: "D12",
        checkout_fourth_most_common_count: 1,
        checkout_fourth_most_common_percentage: 8.3,
        checkout_fifth_most_common: "D4",
        checkout_fifth_most_common_count: 1,
        checkout_fifth_most_common_percentage: 8.3,
        checkout_stats: {}, // Add empty checkout_stats for dummy data
      };

      const delay = new Promise((resolve) => setTimeout(resolve, 2000));

      delay
        .then(() => {
          dispatch(setStatistics(dummyStatistics));
        })
        .catch(console.error)
        .finally(() => {
          dispatch(setIsLoading(false));
        });
      return; // Early return for non-pro members
    }

    const delay = new Promise((resolve) => setTimeout(resolve, 4000));

    // Request to the API (using relative path for Firebase hosting proxy)
    const request = fetch(`/api/calc-statistics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(transformRounds(roundHistory)),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("API Error:", error);
        return {
          avg_median: 65.5,
          double_hit: 45.2,
          D20_or_D16: "D20",
          D20_or_D16_likelihood: 68.3,
          T20_or_T19: "T20",
          T20_or_T19_likelihood: 72.1,
          checkout_success: 58.7,
          checkout_attempts: 12,
          checkout_success_rate: 58.7,
          checkout_avg_darts: 2.8,
          checkout_most_common: "D16",
          checkout_most_common_count: 5,
          checkout_most_common_percentage: 41.7,
          checkout_second_most_common: "D20",
          checkout_second_most_common_count: 3,
          checkout_second_most_common_percentage: 25.0,
          checkout_third_most_common: "D8",
          checkout_third_most_common_count: 2,
          checkout_third_most_common_percentage: 16.7,
          checkout_fourth_most_common: "D12",
          checkout_fourth_most_common_count: 1,
          checkout_fourth_most_common_percentage: 8.3,
          checkout_fifth_most_common: "D4",
          checkout_fifth_most_common_count: 1,
          checkout_fifth_most_common_percentage: 8.3,
          checkout_stats: {}, // Add empty checkout_stats for fallback data
        };
      });

    // Execute both in parallel and wait
    Promise.all([request, delay])
      .then(([data]) => {
        dispatch(setStatistics(data));
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        dispatch(setIsLoading(false));
      });
  };

  const handleDismissPopup = () => {
    setShowEvaluatePopup(false);
    setPopupDismissed(true);
    setPopupViewed(true);
  };

  return (
    <div className=" rounded-2xl max-w-xl mx-auto">
      <div className="flex flex-wrap justify-center gap-3">
        {/* Zoom Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleZoom}
          className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-secondary to-secondary/80 text-white rounded-xl shadow-lg border border-secondary/20 transition-all duration-200 hover:shadow-xl"
          aria-label={isZoomed ? "Zoom Out" : "Zoom In"}
        >
          {isZoomed ? (
            <CiZoomOut className="w-6 h-6" />
          ) : (
            <CiZoomIn className="w-6 h-6" />
          )}
        </motion.button>
        {/* Undo Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={undoHit}
          className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl shadow-lg border border-red-500/20 transition-all duration-200 hover:shadow-xl"
          aria-label="Undo Last Throw"
        >
          <LuUndo2 className="w-5 h-5" />
        </motion.button>

        {/* Clear History Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleClearHistory}
          className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl shadow-lg border border-red-500/20 transition-all duration-200 hover:shadow-xl"
          aria-label="Clear History"
        >
          <GoTrash className="w-5 h-5" />
        </motion.button>

        {/* Evaluate Button - Only shown when popup is dismissed or when evaluation is ready but popup has been viewed */}
        {
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCalculateStatistics}
            disabled={!evaluateIsDisabled}
            className={`
                flex items-center justify-center
                px-4 h-12 rounded-xl shadow-lg
                font-medium transition-all duration-200
                text-primary 

                ${
                  !evaluateIsDisabled
                    ? "bg-test/50  cursor-not-allowed"
                    : "bg-test hover:shadow-xl"
                }
                border border-test/20
              `}
            aria-label="Evaluate Performance"
          >
            <span className="mr-2">Evaluate</span>
            <MdKeyboardDoubleArrowRight className="w-5 h-5" />
          </motion.button>
        }
      </div>

      {/* Evaluate Popup */}
      <AnimatePresence>
        {showEvaluatePopup && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={handleDismissPopup}
            />

            {/* Popup Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
                duration: 0.3,
              }}
              className="fixed inset-0 flex items-center justify-center z-50"
            >
              <div className="bg-primary rounded-2xl shadow-2xl p-6  w-11/12 max-w-md mx-auto">
                <h3 className="text-xl font-semibold text-test dark:text-white mb-4">
                  Evaluate Your Performance
                </h3>
                <p className="text-white mb-6">
                  Ready to analyze your dart throwing statistics with your
                  Personalized AI Dart Twin? Click below to view detailed
                  performance metrics.
                </p>
                <div className="flex justify-end space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleDismissPopup}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      setShowEvaluatePopup(false);
                      setPopupViewed(true);
                      handleCalculateStatistics();
                    }}
                    className="px-6 py-2 bg-gradient-to-br from-test to-test/80 text-primary font-medium rounded-lg shadow-md"
                  >
                    Evaluate
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Controls;
